import {
  formatArrayAttribute,
  formatArrayObjectAttribute,
  formatObjectAttribute,
  getDisplayName,
  getDisplayValue,
  isComplexField,
} from '../../schema/helper';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { createSearchParams, Link } from 'react-router';
import {
  InternalApiAttributeType,
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiPersonCategory,
  InternalApiSearchOperator,
} from '../../api-internal';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { SalvIconButton } from '@salvapp/ui-components';
import { showFeedback } from '../../feedback/feedbackReducer';
import { copyToClipboard } from '../../common/clipboard';
import { useDispatch } from 'react-redux';
import { SearchParamKeys } from '../../common/searchParamKeys';
import ComplexValue from './attributes/ComplexValue';
import { InternalNavigation } from '../../navigation/helper';
import { getHighlightedValue } from './attributes/highlighters';

interface PersonAttributeRowProps {
  fieldName: string;
  index: number;
  array: string[];
  attributes: { [key: string]: string };
  personSchemaFields: InternalApiEntitySchemaField[] | undefined;
  highlightedFields?: string[];
  counterparty?: boolean;
}

export const PersonAttributeRow = ({
  fieldName,
  index,
  array,
  attributes,
  personSchemaFields,
  highlightedFields,
  counterparty = false,
}: PersonAttributeRowProps) => {
  const dispatch = useDispatch();
  const parentSchemaFields = personSchemaFields?.filter((schemaField) => !schemaField.parentId);
  const parentSchemaField = parentSchemaFields?.find((schemaField) => schemaField.fieldName === fieldName);

  const formattedValue = getDisplayValue(parentSchemaField, attributes[fieldName]);
  const Value = () =>
    highlightedFields?.includes(fieldName)
      ? getHighlightedValue({ text: formattedValue, highlight: formattedValue })
      : formattedValue;

  const getFormattedAttribute = () => {
    if (!parentSchemaField) {
      return attributes[fieldName];
    } else if (parentSchemaField.isMulti && parentSchemaField.type === InternalApiEntitySchemaFieldType.OBJECT) {
      return formatArrayObjectAttribute(parentSchemaField, personSchemaFields, attributes[fieldName]);
    } else if (parentSchemaField.type === InternalApiEntitySchemaFieldType.OBJECT) {
      return formatObjectAttribute(parentSchemaField, personSchemaFields, attributes[fieldName]);
    } else if (parentSchemaField.isMulti) {
      return formatArrayAttribute(parentSchemaField, attributes[fieldName]);
    }

    return attributes[fieldName];
  };

  return (
    <>
      <Row className='align-items-center attribute-row'>
        <Col>
          <h5 className='mb-0'>{getDisplayName(parentSchemaField) || fieldName}</h5>
        </Col>
        <Col md='auto' style={{ display: 'flex', gap: '4px', overflowWrap: 'anywhere' }}>
          <SalvIconButton
            ariaLabel={`Copy ${fieldName} value`}
            icon={faCopy}
            className={'copy-button'}
            onClick={async () => {
              const copied = await copyToClipboard(getFormattedAttribute());
              dispatch(
                showFeedback(
                  copied
                    ? { message: 'Value copied', success: true }
                    : { message: 'Could not copy to clipboard', success: false }
                )
              );
            }}
          />
          <span className='small'>
            {isComplexField(
              parentSchemaFields?.find((parentSchemaField) => parentSchemaField.fieldName === fieldName)
            ) ? (
              <ComplexValue
                fieldName={fieldName}
                childAttributesAsString={attributes[fieldName]}
                parentSchemaFields={parentSchemaFields}
                schemaFields={personSchemaFields}
                highlightedFields={highlightedFields}
              />
            ) : parentSchemaField?.searchable ? (
              <Link
                to={{
                  pathname: InternalNavigation.personSearch,
                  search: createSearchParams({
                    [SearchParamKeys.category]: counterparty
                      ? InternalApiPersonCategory.COUNTERPARTY
                      : InternalApiPersonCategory.CUSTOMER,
                    [SearchParamKeys.attributeIdentifier]: parentSchemaField!.id,
                    [SearchParamKeys.attributeType]: InternalApiAttributeType.SCHEMA_FIELD,
                    [SearchParamKeys.operator]: InternalApiSearchOperator.IS_EXACTLY,
                    [SearchParamKeys.query]: attributes[fieldName],
                  }).toString(),
                }}
              >
                <Value />
              </Link>
            ) : (
              <span>
                <Value />
              </span>
            )}
          </span>
        </Col>
      </Row>
      {index < array.length - 1 && <div className={'horizontal-separator-deprecated'} />}
    </>
  );
};

export default PersonAttributeRow;
